import React from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { Trans, useTranslation } from 'react-i18next'

import { Hug, pxToRem, spacing, Stack, Text } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { Region, regionConfig } from '@cash-web/shared-data-access-region-provider'

import { Alias } from './aliasUtils'

const ChameleonLink = styled.a`
  color: inherit;
`

const Link = ({ children, link, ariaLabel }: React.PropsWithChildren<{ link: string; ariaLabel: string }>) => (
  <Text typography="linkSmall" color="textProminent">
    <ChameleonLink href={link} target="_blank" rel="noreferrer" aria-label={ariaLabel}>
      {children}
    </ChameleonLink>
  </Text>
)

export function LegalText({
  region = 'US',
  aliasType,
  isTaxLogin,
}: {
  region: Region
  aliasType: Alias['type']
  isTaxLogin: boolean
}) {
  const theme = useTheme()
  const { t } = useTranslation()

  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const {
    links: { terms, eSignConsent, privacyPolicy, disclosures },
    showDisclosuresOnLogin,
  } = regionConfig[region]

  const sectionSpacing = pxToRem(14)

  if (isTaxLogin) {
    if (aliasType !== 'phone') {
      return null
    }
    return (
      <Hug maxWidth={theme.maxWidths.content}>
        <Text typography="bodySmall" color="textSubtle">
          {t(
            'By entering and clicking Continue, you agree to receive a one time password confirmation code and informational texts from Cash App. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to cancel.',
            { context: 'Legal text for phone input on tax login' }
          )}
        </Text>
      </Hug>
    )
  }

  return (
    <Hug maxWidth={theme.maxWidths.content}>
      <Stack space={spacing.headerSpacing}>
        <Stack space={sectionSpacing}>
          <Text typography="bodySmall" color="textSubtle">
            <Trans
              i18nKey={
                'By entering and clicking Continue, you agree to the <Terms>Terms</Terms>, <EsignConsent>E-Sign Consent</EsignConsent>, & <PrivacyPolicy>Privacy Policy</PrivacyPolicy>.'
              }
              context="Footer text. Links to terms of service, e-sign consent, and privacy policy."
              components={{
                Terms: (
                  <Link
                    link={terms}
                    key={terms}
                    ariaLabel={t('URL link to terms of service.', { context: 'Terms of service link' })}
                  />
                ),
                EsignConsent: (
                  <Link
                    link={eSignConsent}
                    key={eSignConsent}
                    ariaLabel={t('URL link to e-sign consent.', { context: 'e-sign consent link' })}
                  />
                ),
                PrivacyPolicy: (
                  <Link
                    link={privacyPolicy}
                    key={privacyPolicy}
                    ariaLabel={t('URL link to privacy policy.', { context: 'Privacy policy link' })}
                  />
                ),
              }}
            />
          </Text>
          {aliasType === 'phone' && (
            <Text typography="bodySmall" color="textSubtle">
              {t(
                'By entering and clicking Continue, you also agree to receive a one time password confirmation code and informational texts from Cash App. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to cancel.',
                { context: 'Legal text for phone input' }
              )}
            </Text>
          )}
        </Stack>

        {showDisclosuresOnLogin && !isArcadeMigrationEnabled ? (
          <Stack space={sectionSpacing}>
            <Text typography="bodySmall" color="textSubtle">
              {t(
                'Cash App is a financial platform, not a bank. Banking services provided by Cash App’s bank partner(s). Prepaid debit cards issued by Sutton Bank, Member FDIC.',
                { context: 'Legal text - first paragraph' }
              )}
            </Text>
            <Text typography="bodySmall" color="textSubtle">
              <Trans
                i18nKey={
                  'Brokerage services by Cash App Investing LLC, member FINRA/SIPC, subsidiary of Block, Inc., formerly known as Square, Inc. Bitcoin services provided by Block, Inc.  Cash App Investing does not trade bitcoin, and Block, Inc. is not a member of FINRA or SIPC. Tax filing preparation services by Cash App Taxes, Inc. For additional information, see the <Disclosures>Disclosures</Disclosures>'
                }
                context="Legal text - second paragraph"
                components={{
                  Disclosures: (
                    <Link
                      link={disclosures}
                      key={disclosures}
                      ariaLabel={t('URL link to disclosures.', { context: 'Link label' })}
                    />
                  ),
                }}
              />
            </Text>
          </Stack>
        ) : null}
      </Stack>
    </Hug>
  )
}
